// Follower-band tiers + shared report-stat math used by the Compare page,
// the Creator Tiers card and the Top Performers analysis. All computed from
// the already-stored report dataset — works on every existing report with
// zero provider calls.

export const FOLLOWER_TIERS = [
  { id: "nano", label: "Nano", hint: "0 – 10K followers" },
  { id: "micro", label: "Micro", hint: "10K – 100K followers" },
  { id: "macro", label: "Macro", hint: "100K – 1M followers" },
  { id: "mega", label: "Mega", hint: "1M – 10M followers" },
  { id: "celebrity", label: "Celebrity", hint: "10M+ followers" },
];

export const followerTier = (followers) => {
  const f = Number(followers || 0);
  if (f >= 10000000) return "celebrity";
  if (f >= 1000000) return "mega";
  if (f >= 100000) return "macro";
  if (f >= 10000) return "micro";
  return "nano";
};

export const formatNum = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${Math.round(v * 100) / 100}`;
};

export const fmtPct = (v) => (v === null || v === undefined ? "N/A" : `${v.toFixed(2)}%`);

/**
 * Full stat block for one report dataset — mirrors the report Stats & ER tab
 * formulas exactly (per-post average ER cards, per-bucket deliverable table,
 * posted-vs-not-posted split, engagement totals, creator tiers).
 */
export const computeReportStats = (posts = [], summary = []) => {
  // Dashboard counts = sum of per-creator summary rows (canonical rule —
  // same basis as every export). Collab is an attribute already inside its
  // type count on the attributed creator; never a separate bucket.
  const counts = { reels: 0, carousels: 0, static: 0, videos: 0, collabs: 0, stories: 0, highlights: 0, live: 0 };
  for (const s of summary) {
    counts.reels += s.reels || 0;
    counts.carousels += s.carousels || 0;
    counts.static += s.static || 0;
    counts.videos += s.videos || 0;
    counts.collabs += s.collab_posts || 0;
    counts.stories += s.stories || 0;
    counts.highlights += s.stories_highlights || 0;
    counts.live += s.stories_live || 0;
  }
  const postTotal = counts.reels + counts.carousels + counts.static + counts.videos;
  const grandTotal = postTotal + counts.live;

  let views = 0, likes = 0, comments = 0, shares = 0, hasShares = false;
  for (const p of posts) {
    views += Number(p.video_views || 0);
    likes += Number(p.likes || 0);
    comments += Number(p.comments || 0);
    if (p.shares !== null && p.shares !== undefined) { shares += Number(p.shares || 0); hasShares = true; }
  }
  const reach = summary.reduce((n, s) => n + (Number(s.reach) || 0), 0);

  // ER cards — AGGREGATE (user's formula): Σ(likes+comments+shares) ÷ base,
  // posts deduped by shortcode so collab rows count a post once.
  //   Reels base  = Σ views;  Static base = Σ owner followers per post.
  const followers = {};
  for (const s of summary) followers[s.handle] = Number(s.followers || 0);
  const seen = new Map();
  // Prefer the row whose handle has KNOWN followers (tracked collaborator)
  // over the untracked owner row — Static ER needs that follower base.
  const rowScore = (x) => (((followers[x.handle] || 0) > 0 ? 2 : 0) + (!x.is_collab ? 1 : 0));
  for (const p of posts) {
    const k = p.shortcode || p.post_url || p.id;
    if (!k) continue;
    const prev = seen.get(k);
    if (!prev || rowScore(p) > rowScore(prev)) seen.set(k, p);
  }
  let rEng = 0, rViews = 0, sEng = 0, sBase = 0;
  for (const p of seen.values()) {
    const t = p.post_type || "";
    const lk = Number(p.likes || 0), cm = Number(p.comments || 0);
    const sh = p.shares == null ? 0 : Number(p.shares || 0);
    if (t === "Reel" || t === "Video") {
      const vw = Number(p.video_views || 0);
      if (vw > 0) { rEng += lk + cm + sh; rViews += vw; }
    } else if (t === "Static" || t.startsWith("Carousel")) {
      const f = followers[p.handle] || 0;
      if (f > 0) { sEng += lk + cm + sh; sBase += f; }
    }
  }
  const erReels = rViews > 0 ? (rEng / rViews) * 100 : null;
  const erStatic = sBase > 0 ? (sEng / sBase) * 100 : null;

  // Deliverable table — per-post average ER per bucket (identical to the
  // report's Deliverable Type table).
  const buckets = { Reels: [], "Static Posts": [], Carousels: [] };
  for (const p of posts) {
    const t = p.post_type || "";
    if (t === "Reel" || t === "Video") buckets.Reels.push(p);
    else if (t === "Static") buckets["Static Posts"].push(p);
    else if (t.startsWith("Carousel")) buckets.Carousels.push(p);
  }
  const deliverables = Object.entries(buckets).map(([key, arr]) => {
    let L = 0, C = 0, V = 0, S = 0, hs = false;
    for (const p of arr) {
      const lk = Number(p.likes || 0), cm = Number(p.comments || 0), vw = Number(p.video_views || 0);
      const hasS = p.shares !== null && p.shares !== undefined;
      const sh = hasS ? Number(p.shares || 0) : 0;
      L += lk; C += cm; V += vw;
      if (hasS) { S += sh; hs = true; }
    }
    // ER — aggregate, deduped by shortcode preferring rows with known
    // followers (tracked collaborator over untracked owner).
    const seenD = new Map();
    for (const p of arr) {
      const k = p.shortcode || p.post_url || p.id;
      if (!k) continue;
      const prev = seenD.get(k);
      if (!prev || rowScore(p) > rowScore(prev)) seenD.set(k, p);
    }
    let erEng = 0, erBase = 0;
    for (const p of seenD.values()) {
      const lk = Number(p.likes || 0), cm = Number(p.comments || 0);
      const sh = p.shares == null ? 0 : Number(p.shares || 0);
      if (key === "Reels") {
        const vw = Number(p.video_views || 0);
        if (vw > 0) { erEng += lk + cm + sh; erBase += vw; }
      } else {
        const f = followers[p.handle] || 0;
        if (f > 0) { erEng += lk + cm + sh; erBase += f; }
      }
    }
    return { type: key, published: arr.length, views: V, likes: L, comments: C,
             shares: hs ? S : null, er: erBase > 0 ? (erEng / erBase) * 100 : null };
  });

  const total = summary.length;
  const posted = summary.filter((s) => (s.matched_posts || 0) > 0).length;
  const postedWithStories = summary.filter((s) => (s.matched_posts || 0) > 0 && (s.stories || 0) > 0).length;
  const bif = {
    total,
    posted,
    postedWithStories,
    postedOnly: posted - postedWithStories,
    storiesOnly: summary.filter((s) => (s.matched_posts || 0) === 0 && (s.stories || 0) > 0).length,
    nothing: summary.filter((s) => (s.matched_posts || 0) === 0 && (s.stories || 0) === 0).length,
  };

  const tiers = {};
  for (const t of FOLLOWER_TIERS) tiers[t.id] = 0;
  for (const s of summary) tiers[followerTier(s.followers)]++;

  return { counts, postTotal, grandTotal, totals: { views, likes, comments, shares, hasShares },
           reach, erReels, erStatic, deliverables, bif, tiers };
};
